import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { cn } from '../utils/cn';

type BentoDensity = 'wide' | 'medium' | 'narrow';

const MEDIUM_QUERY = '(max-width: 1100px)';
const NARROW_QUERY = '(max-width: 640px)';

function readDensity(): BentoDensity {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'wide';
  if (window.matchMedia(NARROW_QUERY).matches) return 'narrow';
  if (window.matchMedia(MEDIUM_QUERY).matches) return 'medium';
  return 'wide';
}

/**
 * The collapse is driven from JS rather than media queries because the column
 * count is a prop: a CSS rule cannot know that this particular grid was built
 * on 12 columns and should halve rather than fix itself at six.
 */
function useBentoDensity(): BentoDensity {
  const [density, setDensity] = useState<BentoDensity>(readDensity);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const medium = window.matchMedia(MEDIUM_QUERY);
    const narrow = window.matchMedia(NARROW_QUERY);
    const update = () => setDensity(readDensity());
    medium.addEventListener('change', update);
    narrow.addEventListener('change', update);
    update();
    return () => {
      medium.removeEventListener('change', update);
      narrow.removeEventListener('change', update);
    };
  }, []);

  return density;
}

interface BentoContextValue {
  /** Whether cells apply the CSS entrance. Read by BentoCell. */
  stagger?: boolean;
  /** Tracks actually rendered at this width. */
  columns: number;
  /** The grid's declared desktop track count, used to judge a cell's intent. */
  baseColumns: number;
  density: BentoDensity;
}

const BentoContext = createContext<BentoContextValue>({
  columns: 12,
  baseColumns: 12,
  density: 'wide',
  stagger: true,
});

export interface BentoGridProps extends HTMLMotionProps<'div'> {
  /** Desktop track count. 12 divides into halves, thirds and quarters. */
  columns?: number;
  gap?: number;
  /** Floor for an auto row, which is what makes a 2-row cell visibly taller. */
  rowMinHeight?: number;
  /** Set false when the grid is not the screen's entrance — a filtered redraw. */
  stagger?: boolean;
  children?: ReactNode;
}

export function BentoGrid({
  columns = 12,
  gap = 16,
  rowMinHeight = 132,
  stagger = true,
  className,
  style,
  children,
  ...rest
}: BentoGridProps) {
  const density = useBentoDensity();
  /**
   * Narrow is two tracks, not one. A phone forced to one track turns eight KPI
   * tiles into eight full screens of scrolling, and a figure with a label is
   * perfectly legible at half of 375pt. Cells that were wide on desktop still
   * take the full width — see BentoCell.
   */
  const effectiveColumns =
    density === 'narrow'
      ? 2
      : density === 'medium'
        ? Math.max(2, Math.round(columns / 2))
        : columns;

  // Rows are shorter on a phone for the same reason: the floor exists to keep
  // a sparse desktop tile from collapsing, not to pad a stack of small ones.
  const effectiveRowMin = density === 'narrow' ? Math.min(rowMinHeight, 92) : rowMinHeight;

  return (
    <BentoContext.Provider
      value={{ columns: effectiveColumns, baseColumns: columns, density, stagger }}
    >
      <motion.div
        className={cn('grid min-w-0', className)}
        style={{
          gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
          gridAutoRows: `minmax(${effectiveRowMin}px, auto)`,
          // Dense flow is what lets a 1×1 backfill the hole a 2×2 leaves.
          gridAutoFlow: 'dense',
          gap,
          ...style,
        }}
        {...rest}
      >
        {children}
      </motion.div>
    </BentoContext.Provider>
  );
}

export interface BentoCellProps extends HTMLMotionProps<'div'> {
  /** Column span at desktop width. Clamped as the grid collapses. */
  col?: number;
  row?: number;
  /** Position in the cascade. Omit and the cell simply rises without a delay. */
  index?: number;
  children?: ReactNode;
}

export function BentoCell({
  col = 1,
  row = 1,
  index,
  className,
  style,
  children,
  ...rest
}: BentoCellProps) {
  const { columns, baseColumns, density, stagger } = useContext(BentoContext);

  /**
   * At narrow width a cell keeps only the distinction that survives a phone:
   * whether it was a *panel* or a *tile*. Anything that occupied half the
   * desktop grid or more — a chart, a table, a hierarchy — still needs the
   * full width. Everything smaller pairs up two to a row.
   */
  const colSpan =
    density === 'narrow' ? (col >= baseColumns / 2 ? 2 : 1) : Math.min(Math.max(1, col), columns);

  // Row spans are a desktop composition device; on a phone every cell is as
  // tall as its content.
  const rowSpan = density === 'narrow' ? 1 : Math.max(1, row);

  return (
    <motion.div
      // The entrance is CSS, not a motion variant: a variant makes opacity 0 the
      // resting DOM state, and an interrupted animation then strands the cell
      // invisible forever. See entrance.css.
      className={cn(
        'flex min-w-0 flex-col',
        stagger && 'g-rise',
        stagger && index !== undefined && index < 10 ? `g-rise-${index + 1}` : null,
        className,
      )}
      style={{
        gridColumn: `span ${colSpan} / span ${colSpan}`,
        gridRow: `span ${rowSpan} / span ${rowSpan}`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
